/**
 * Custom Collection Carousel Web Component
 * Handles carousel scrolling, dots, arrows, autoplay, and infinity loop
 */
if (!customElements.get('custom-collection-carousel')) {
    class CustomCollectionCarousel extends HTMLElement {
        constructor() {
            super();
            this.track = null;
            this.items = [];
            this.dots = [];
            this.currentIndex = 0;
            this.autoplayInterval = null;
            this.isInfinite = false;
            this.cloneCount = 0;
        }

        connectedCallback() {
            this.track = this.querySelector('.custom-cl__carousel-track');
            if (!this.track) return;

            this.items = Array.from(this.track.children);
            this.dots = Array.from(this.querySelectorAll('.custom-cl__dot'));
            this.prevBtn = this.querySelector('.custom-cl__arrow--prev');
            this.nextBtn = this.querySelector('.custom-cl__arrow--next');
            this.isInfinite = this.dataset.infinity === 'true';
            this.autoplay = this.dataset.autoplay === 'true';
            this.autoplaySpeed = parseInt(this.dataset.autoplaySpeed) || 4000;

            this.setupArrows();
            this.setupDots();
            this.setupScrollListener();

            if (this.isInfinite && this.items.length > 0) {
                this.setupInfiniteLoop();
            }

            if (this.autoplay) {
                this.startAutoplay();
                this.addEventListener('mouseenter', () => this.stopAutoplay());
                this.addEventListener('mouseleave', () => this.startAutoplay());
            }

            if (this.dataset.reveal === 'true') {
                this.setupReveal();
            }

            this.updateDots();
        }

        disconnectedCallback() {
            this.stopAutoplay();
        }

        getVisibleCount() {
            if (!this.track || this.items.length === 0) return 1;
            var trackWidth = this.track.offsetWidth;
            var itemWidth = this.items[0].offsetWidth;
            if (itemWidth === 0) return 1;
            var gap = parseFloat(getComputedStyle(this.track).gap) || 0;
            return Math.round(trackWidth / (itemWidth + gap)) || 1;
        }

        getItemScrollWidth() {
            if (this.items.length === 0) return 0;
            var gap = parseFloat(getComputedStyle(this.track).gap) || 0;
            return this.items[0].offsetWidth + gap;
        }

        getTotalSlides() {
            var realItems = this.isInfinite ? this.items.length - this.cloneCount * 2 : this.items.length;
            return Math.max(1, realItems);
        }

        setupArrows() {
            if (this.prevBtn) {
                this.prevBtn.addEventListener('click', () => this.goToPrev());
            }
            if (this.nextBtn) {
                this.nextBtn.addEventListener('click', () => this.goToNext());
            }
        }

        setupDots() {
            this.dots.forEach((dot, index) => {
                dot.addEventListener('click', () => this.goToSlide(index));
            });
        }

        setupScrollListener() {
            var scrollTimeout;
            this.track.addEventListener('scroll', () => {
                clearTimeout(scrollTimeout);
                scrollTimeout = setTimeout(() => {
                    this.updateDots();
                    if (this.isInfinite) {
                        this.handleInfiniteScroll();
                    }
                }, 50);
            }, { passive: true });
        }

        goToSlide(index) {
            var itemWidth = this.getItemScrollWidth();
            var offset = this.isInfinite ? (index + this.cloneCount) * itemWidth : index * itemWidth;
            this.track.scrollTo({ left: offset, behavior: 'smooth' });
            this.currentIndex = index;
            this.updateDots();
        }

        goToNext() {
            var totalSlides = this.getTotalSlides();
            var visibleCount = this.getVisibleCount();
            var maxIndex = Math.max(0, totalSlides - visibleCount);

            if (this.isInfinite) {
                this.currentIndex++;
                if (this.currentIndex >= totalSlides) {
                    this.currentIndex = 0;
                }
                this.goToSlide(this.currentIndex);
            } else {
                if (this.currentIndex >= maxIndex) {
                    this.currentIndex = 0;
                } else {
                    this.currentIndex = Math.min(this.currentIndex + 1, maxIndex);
                }
                this.goToSlide(this.currentIndex);
            }
        }

        goToPrev() {
            var totalSlides = this.getTotalSlides();

            if (this.isInfinite) {
                this.currentIndex--;
                if (this.currentIndex < 0) {
                    this.currentIndex = totalSlides - 1;
                }
                this.goToSlide(this.currentIndex);
            } else {
                this.currentIndex = Math.max(0, this.currentIndex - 1);
                this.goToSlide(this.currentIndex);
            }
        }

        updateDots() {
            var itemWidth = this.getItemScrollWidth();
            if (itemWidth === 0) return;

            var scrollLeft = this.track.scrollLeft;
            var activeIndex;
            var totalSlides = this.getTotalSlides();
            var maxIndex = this.isInfinite ? totalSlides - 1 : Math.max(0, totalSlides - this.getVisibleCount());

            if (this.isInfinite) {
                activeIndex = Math.round(scrollLeft / itemWidth) - this.cloneCount;
                if (activeIndex < 0) activeIndex = totalSlides + activeIndex;
                if (activeIndex >= totalSlides) activeIndex = activeIndex - totalSlides;
            } else {
                activeIndex = Math.round(scrollLeft / itemWidth);
            }

            this.currentIndex = activeIndex;

            this.dots.forEach((dot, index) => {
                if (!this.isInfinite && index > maxIndex) {
                    dot.style.display = 'none';
                } else {
                    dot.style.display = '';
                }
                dot.classList.toggle('custom-cl__dot--active', index === activeIndex);
            });
        }

        setupInfiniteLoop() {
            var visibleCount = this.getVisibleCount();
            this.cloneCount = Math.min(visibleCount, this.items.length);

            for (var i = this.items.length - 1; i >= this.items.length - this.cloneCount; i--) {
                var clone = this.items[i].cloneNode(true);
                clone.setAttribute('aria-hidden', 'true');
                clone.classList.add('custom-cl__clone');
                clone.querySelectorAll('a, button, input, select, textarea, [tabindex]').forEach(el => el.setAttribute('tabindex', '-1'));
                this.track.insertBefore(clone, this.track.firstChild);
            }

            for (var j = 0; j < this.cloneCount; j++) {
                var clone2 = this.items[j].cloneNode(true);
                clone2.setAttribute('aria-hidden', 'true');
                clone2.classList.add('custom-cl__clone');
                clone2.querySelectorAll('a, button, input, select, textarea, [tabindex]').forEach(el => el.setAttribute('tabindex', '-1'));
                this.track.appendChild(clone2);
            }

            this.items = Array.from(this.track.children);

            requestAnimationFrame(() => {
                var itemWidth = this.getItemScrollWidth();
                this.track.scrollLeft = this.cloneCount * itemWidth;
            });
        }

        handleInfiniteScroll() {
            var itemWidth = this.getItemScrollWidth();
            if (itemWidth === 0) return;
            var totalRealItems = this.getTotalSlides();
            var scrollLeft = this.track.scrollLeft;
            var cloneStartOffset = this.cloneCount * itemWidth;
            var realEndOffset = (this.cloneCount + totalRealItems) * itemWidth;

            if (scrollLeft <= 0) {
                this.track.style.scrollBehavior = 'auto';
                this.track.scrollLeft = realEndOffset - itemWidth;
                this.track.style.scrollBehavior = '';
            } else if (scrollLeft >= realEndOffset) {
                this.track.style.scrollBehavior = 'auto';
                this.track.scrollLeft = cloneStartOffset;
                this.track.style.scrollBehavior = '';
            }
        }

        startAutoplay() {
            this.stopAutoplay();
            this.autoplayInterval = setInterval(() => this.goToNext(), this.autoplaySpeed);
        }

        stopAutoplay() {
            if (this.autoplayInterval) {
                clearInterval(this.autoplayInterval);
                this.autoplayInterval = null;
            }
        }

        setupReveal() {
            var revealItems = this.querySelectorAll('.custom-cl__reveal-item');
            if (revealItems.length === 0) return;

            var observer = new IntersectionObserver(
                function (entries) {
                    entries.forEach(function (entry) {
                        if (entry.isIntersecting) {
                            entry.target.classList.add('custom-cl__reveal-item--visible');
                            observer.unobserve(entry.target);
                        }
                    });
                },
                { threshold: 0.1, rootMargin: '0px 0px -50px 0px' }
            );

            revealItems.forEach(function (item, index) {
                item.style.transitionDelay = (index * 0.08) + 's';
                observer.observe(item);
            });
        }
    }

    customElements.define('custom-collection-carousel', CustomCollectionCarousel);
}
